"use client";

import { cn, type PolymorphProps } from "logic/utils";
import type { ElementType, ReactNode } from "react";


import { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANTS, type IconButtonSize, type IconButtonVariant } from "./variants";

interface IconButtonExtraProps {
  /**
   * Color variant. Matches Figma `comp/button` 1:1 — same variant grammar as
   * {@link Button}.
   * @default "outline"
   */
  variant?: IconButtonVariant;
  /**
   * Container size — `sm` = 32px, `md` = 36px, `lg` = 40px. Matches Figma's
   * `size` variant.
   * @default "md"
   */
  size?: IconButtonSize;
}

/**
 * Polymorphic props for `<IconButton>`. Defaults to `<button>`; pass
 * `as={Link}`, `as="a"`, etc. to change the rendered element while preserving
 * all native props for that element.
 */
export type IconButtonProps<T extends ElementType> = PolymorphProps<T, IconButtonExtraProps>;

/**
 * Design-system icon button — a square tile wrapping a single icon, sharing
 * its color/state tokens with {@link Button} (same 6 variants × 3 sizes
 * grammar) so it reads as the icon-only sibling of the same control.
 *
 * @example Default (outline, md)
 * ```tsx
 * <IconButton aria-label="Close" onClick={onClose}>
 *   <XIcon className="size-5" />
 * </IconButton>
 * ```
 *
 * @example Primary variant
 * ```tsx
 * <IconButton aria-label="Confirm" variant="primary" onClick={onConfirm}>
 *   <CheckIcon className="size-5" />
 * </IconButton>
 * ```
 *
 * @example Large size
 * ```tsx
 * <IconButton aria-label="Expand" size="lg" onClick={onExpand}>
 *   <ExpandIcon className="size-5" />
 * </IconButton>
 * ```
 *
 * @example Polymorphic — Next `Link`
 * ```tsx
 * <IconButton as={Link} aria-label="View loan detail" href={`/loan/${documentId}`} variant="outline" size="sm">
 *   <Icon name="caret_right" size="small" />
 * </IconButton>
 * ```
 *
 * @example Polymorphic — plain anchor
 * ```tsx
 * <IconButton as="a" aria-label="Open docs" href="https://docs.usd.ai" target="_blank">
 *   <ExternalLinkIcon className="size-5" />
 * </IconButton>
 * ```
 *
 * @see Figma — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/USD.AI-%7C-Design-System?node-id=564-4170&m=dev
 */
export function IconButton<T extends ElementType = "button">(props: IconButtonProps<T>) {
  const {
    as: Component = "button",
    variant = "outline",
    size = "md",
    disabled,
    className,
    children,
    componentRef,
    ...rest
  } = props;

  const tokens = ICON_BUTTON_VARIANTS[variant];

  return (
    <Component
      ref={componentRef}
      type={Component == "button" ? "button" : undefined}
      {...rest}
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-button outline-hidden",
        ICON_BUTTON_SIZES[size],
        tokens.base,
        "before:absolute before:inset-0 before:rounded-[inherit] before:transition-colors before:content-['']",
        tokens.stateHover,
        tokens.stateFocus,
        tokens.statePressed,
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      disabled={disabled}
    >
      {/* The icon to render, centered in the 20px slot (e.g. <SidebarCollapseIcon className="size-5" />). */}
      {children as ReactNode}
    </Component>
  );
}
